/**
 * PRESS — trigger the browser's native print/PDF flow for a StudyDoc.
 *
 * Print CSS (studyDocPrint.css) targets `[data-study-doc-print-root]` via a
 * `:has()` media-print rule, so no JS-side class toggling is needed to arm
 * it — native ⌘P works exactly like this explicit button. Plot embeds
 * rasterize themselves on mount (`StudyDocResolvedPlotEmbed` in
 * StudyDocBody.tsx) and keep a print-only `<img>` fallback in the DOM at
 * all times, so `window.print()` alone is enough here too.
 *
 * Chrome/Safari paint their own header from `document.title` in Times.
 * That slot cannot be restyled, so print blanks the title (button and ⌘P)
 * and the running header in the page uses a system sans stack instead.
 */

import './studyDocPrint.css'

export interface PrintStudyDocOptions {
  title?: string
}

const PRINT_TITLE_BLANK = '\u00A0'
const TITLE_RESTORE_ATTR = 'data-study-doc-print-title-restore'

function blankDocumentTitle() {
  if (document.documentElement.hasAttribute(TITLE_RESTORE_ATTR)) {
    return
  }
  document.documentElement.setAttribute(TITLE_RESTORE_ATTR, document.title)
  document.title = PRINT_TITLE_BLANK
}

function restoreDocumentTitle() {
  const previous = document.documentElement.getAttribute(TITLE_RESTORE_ATTR)
  if (previous == null) {
    return
  }
  document.title = previous
  document.documentElement.removeAttribute(TITLE_RESTORE_ATTR)
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeprint', blankDocumentTitle)
  window.addEventListener('afterprint', restoreDocumentTitle)
}

export async function printStudyDoc(
  _options: PrintStudyDocOptions = {},
): Promise<void> {
  blankDocumentTitle()
  window.print()
}
